import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { ControlProps, GroupBase, OptionProps, SelectInstance, components as reactSelectComponents } from 'react-select'
import { AsyncPaginate, AsyncPaginateProps } from 'react-select-async-paginate'



export type CustomOptionProps<Option, CustomProps,> = OptionProps<Option, boolean, GroupBase<Option>> & {
    selectProps: {
        customProps: CustomProps
    } & OptionProps<Option, boolean, GroupBase<Option>>['selectProps']
}

type BaseProps = AsyncPaginateProps<unknown, GroupBase<unknown>, unknown, boolean>
interface CustomAsyncPaginateProps extends Omit<BaseProps, 'isMulti'|'maxMenuHeight'> {
    /** searchOnly: for displaying and filtering info without selecting */
    searchOnly?: boolean
    /** clearOnSelect: only necessary if wanting to clear when searchOnly is true,
     *  otherwise it should do this anyway
     */
    clearOnSelect?: boolean
    isMulti?: boolean
    keyName?: React.Key
    customProps?: any
    customControlProps?: any
    callbacks?: any
    style?: any
    maxMenuHeight?: number | string
    /** message rendered directly below the input (Control), above the results list */
    formText?: React.ReactNode
}

const CustomAsyncPaginate: React.FC<CustomAsyncPaginateProps> = observer(({
    keyName,
    searchOnly = false,
    clearOnSelect = false,
    className,
    classNamePrefix,
    maxMenuHeight,
    onChange,
    components,
    ...otherProps
}) => {
    const [inputValue, setInputValue] = useState('')

    const selectRef = useRef<SelectInstance<unknown>>(null)
    const inputRef = useRef(null)

    useEffect(() => {
        // Find and store a reference to the input element
        if (selectRef.current) {
            const input = selectRef.current.inputRef
            if (input) {
                inputRef.current = input
            }
        }
    }, [])

    const onChangeHandler = (newValue, actionMeta) => {
        if (clearOnSelect) {
            setInputValue('')
        }
        if (onChange) onChange(newValue, actionMeta)
    }

    /**
     * This is useful for when the react-select element is only being used for searching
     * Otherwise since no item is selected, when clicking away from the search, the value
     * typed in will go away.
     */
    const onInputChange = (textInput, { action }) => {
        if (action === 'input-change') {
            setInputValue(textInput)
            return textInput
        }
        return inputValue
    }

    const handleKeyDown = (event) => {
        const focusedElement = document.activeElement as HTMLElement

        if (event.key === 'Enter' || event.key === ' ') {
            if (focusedElement && focusedElement !== inputRef.current) {
                // Manually trigger click for non-input focused elements
                focusedElement.click()
            }
        }
    }

    /**
     * This should take priority over modals
     * @param event
     */
    const handleKeyEscCapture = (event) => {
        const focusedElement = document.activeElement as HTMLElement
        if (event.key === 'Escape') {
            if (inputRef.current && focusedElement !== inputRef.current) {
                event.stopPropagation()
                inputRef.current.focus()
            }
        }
    }

    return (
        <div
            style={{ display: 'contents' }}
            className="key-capture-wrapper"
            onKeyDownCapture={handleKeyEscCapture}
        >
            <AsyncPaginate
                key={keyName}
                selectRef={selectRef}
                onChange={onChangeHandler}

                {...searchOnly && {
                    inputValue: inputValue,
                    onInputChange: onInputChange,
                    tabSelectsValue: false,
                    onKeyDown: handleKeyDown
                }}

                className={classNames('react-select', className)}
                classNamePrefix="select"

                // @ts-expect-error A string still works eg: calc(100vh - 100px)
                maxMenuHeight={maxMenuHeight}

                debounceTimeout={500}
                components={{
                    ...(otherProps.formText != null && { Control: ControlWithFormText }),
                    ...components
                }}
                // formText stays in otherProps so it lands on selectProps for ControlWithFormText
                {...otherProps}
            />
        </div>
    )
})

type ControlWithFormTextProps = ControlProps<unknown, boolean, GroupBase<unknown>> & {
    selectProps: {
        formText?: React.ReactNode
    } & ControlProps<unknown, boolean, GroupBase<unknown>>['selectProps']
}

/**
 * Renders formText between the input (Control) and the results list. Must stay at module
 * level, otherwise react-select remounts the control on every render and the input loses focus.
 */
const ControlWithFormText = (props: ControlWithFormTextProps) => (
    <>
        <reactSelectComponents.Control {...props} />
        <div
            className="form-text mt-2 ms-1"
            role="status"
            aria-live="polite">
            {props.selectProps.formText}
        </div>
    </>
)

export { CustomAsyncPaginate, CustomAsyncPaginateProps }
